import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { ShippingLocation, Variation, Product, CartItem, DiscountInfo } from '@/types';
import { useStoreInfo } from '@/lib/store';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function parseCurrencyAmount(value: number | string): number {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0;
  }

  const cleaned = String(value).replace(/[^0-9.-]+/g, '');
  const parsed = parseFloat(cleaned);
  return Number.isFinite(parsed) ? parsed : 0;
}

export function roundToTwo(amount: number): number {
  return Math.round((amount + Number.EPSILON) * 100) / 100;
}

export function toCents(value: number | string): number {
  return Math.round(parseCurrencyAmount(value) * 100);
}

export function fromCents(value: number): number {
  return value / 100;
}

export function calculateDiscount(product: Product, totalQuantity: number): DiscountInfo {
  const discountType = product.discount_type;
  const discountOff = parseCurrencyAmount(product.discount_off);
  const discountQty = parseInt(product.discount_qty) || 0;
  
  if (discountOff === 0 || discountQty === 0) {
    return {
      type: 'none',
      amount: 0,
      threshold: 0,
      isEligible: false,
      discountValue: 0
    };
  }
  
  const isEligible = totalQuantity >= discountQty;
  let discountValue = 0;
  
  if (isEligible) {
    const basePriceCents = toCents(product.sellingprice);
    const lineTotalCents = basePriceCents * totalQuantity;
    if (discountType === 'percentage') {
      const discountValueCents = Math.round((lineTotalCents * discountOff) / 100);
      discountValue = fromCents(discountValueCents);
    } else if (discountType === 'fixed') {
      const discountValueCents = toCents(discountOff) * totalQuantity;
      discountValue = fromCents(discountValueCents);
    }
  }
  
  return {
    type: discountType as 'fixed' | 'percentage' | 'none',
    amount: discountOff,
    threshold: discountQty,
    isEligible,
    discountValue: roundToTwo(discountValue)
  };
}

export function getDiscountedPrice(product: Product, quantity: number, variation?: any): number {
  const discount = calculateDiscount(product, quantity);
  const basePrice = variation
    ? parseCurrencyAmount(variation.variation_amount) * quantity
    : parseCurrencyAmount(product.sellingprice) * quantity;
  return Math.max(0, roundToTwo(basePrice - discount.discountValue));
}

export function calculateVariationDiscount(product: Product, variation: any, quantity: number): DiscountInfo {
  const discountType = product.discount_type;
  const discountOff = parseCurrencyAmount(product.discount_off);
  const discountQty = parseInt(product.discount_qty) || 0;

  if (discountOff === 0 || discountQty === 0) {
    return {
      type: 'none',
      amount: 0,
      threshold: 0,
      isEligible: false,
      discountValue: 0
    };
  }

  const isEligible = quantity >= discountQty;
  let discountValue = 0;

  if (isEligible) {
    const variationPriceCents = toCents(variation.variation_amount);
    const lineTotalCents = variationPriceCents * quantity;
    if (discountType === 'percentage') {
      const discountValueCents = Math.round((lineTotalCents * discountOff) / 100);
      discountValue = fromCents(discountValueCents);
    } else if (discountType === 'fixed') {
      const discountValueCents = toCents(discountOff) * quantity;
      discountValue = fromCents(discountValueCents);
    }
  }

  return {
    type: discountType as 'fixed' | 'percentage' | 'none',
    amount: discountOff,
    threshold: discountQty,
    isEligible,
    discountValue: roundToTwo(discountValue)
  };
}

export function hasDiscount(product: Product): boolean {
  const discountOff = parseFloat(product.discount_off) || 0;
  const discountQty = parseInt(product.discount_qty) || 0;
  return discountOff > 0 && discountQty > 0;
}

export function getDiscountBadgeText(product: Product): string {
  if (!hasDiscount(product)) return '';
  
  const discountOff = parseFloat(product.discount_off);
  if (product.discount_type === 'percentage') {
    return `${discountOff}% OFF`;
  } else {
    return `${formatPrice(discountOff)} OFF`;
  }
}

export function calculateCartDiscounts(cart: CartItem[]): {
  totalDiscount: number;
  discountBreakdown: Array<{
    productId: number;
    productName: string;
    discountAmount: number;
    discountType: string;
    threshold: number;
    quantity: number;
  }>;
} {
  let totalDiscount = 0;
  const discountBreakdown: Array<{
    productId: number;
    productName: string;
    discountAmount: number;
    discountType: string;
    threshold: number;
    quantity: number;
  }> = [];
  
  cart.forEach(item => {
    if (item.selectedVariations && item.selectedVariations.length > 0) {
      // Handle variations separately
      item.selectedVariations.forEach(variation => {
        const variationData = item.variations.find(v => v.variation === variation.variation);
        if (variationData) {
          const discount = calculateVariationDiscount(item, variationData, variation.quantity);
          if (discount.isEligible && discount.discountValue > 0) {
            totalDiscount += discount.discountValue;
            discountBreakdown.push({
              productId: item.id,
              productName: `${item.produt_name} (${variation.variation})`,
              discountAmount: discount.discountValue,
              discountType: discount.type,
              threshold: discount.threshold,
              quantity: variation.quantity
            });
          }
        }
      });
    } else {
      // Handle regular products
      const discount = calculateDiscount(item, item.quantity);
      if (discount.isEligible && discount.discountValue > 0) {
        totalDiscount += discount.discountValue;
        discountBreakdown.push({
          productId: item.id,
          productName: item.produt_name,
          discountAmount: discount.discountValue,
          discountType: discount.type,
          threshold: discount.threshold,
          quantity: item.quantity
        });
      }
    }
  });
  
  return { totalDiscount: roundToTwo(totalDiscount), discountBreakdown };
}
// Get current store's domain (e.g., "mystore.cultaz.com" or "localhost:5173")
export function getDomain() {
  return window.location.hostname;
}

// Get the base API endpoint (e.g., "https://api.cultaz.com/API")
export function getApiEndpoint() {
  const apiEndpoint = import.meta.env.VITE_API_ENDPOINT;
  if (apiEndpoint) {
    return apiEndpoint.replace(/\/+$/, ''); // Remove trailing slashes
  }
  // Fallback for development
  return `${window.location.origin}/api`;
}

export function getApiOrigin() {
  try {
    const url = new URL(getApiEndpoint());
    return `${url.protocol}//${url.host}`;
  } catch {
    return window.location.origin;
  }
}

// Get store identifier from domain
export function getStoreId() {
  const domain = window.location.hostname;
  const storeId = domain.split('.')[0];
  return storeId;
}

// Build a complete API URL with proper domain parameter
export function buildApiUrl(endpoint: string) {
  const apiBase = getApiEndpoint();
  const storeDomain = getDomain();
  const separator = endpoint.includes('?') ? '&' : '?';
  return `${apiBase}/${endpoint}${separator}domain=${storeDomain}`;
}

export function resolveImageUrl(path: string | null | undefined) {
  if (!path) return '';
  const trimmed = String(path).trim();
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  const origin = getApiOrigin().replace(/\/+$/, '');
  const cleanPath = trimmed.replace(/^\/+/, '');
  return `${origin}/${cleanPath}`;
}

export function displayPriceWithCurrency(price: string | number) {
  const { storeInfo } = useStoreInfo.getState();
  const currency = storeInfo?.currenCy || 'USD';
  
  // Convert to string if it's a number
  const priceStr = typeof price === 'number' ? price.toString() : price;
  
  // Return price with currency code (e.g., "CAD 3.5")
  return `${currency} ${priceStr}`;
}

export function formatPrice(price: number | string, currency: string = 'USD') {
  const numericPrice = typeof price === 'string' ? parseCurrencyAmount(price) : parseCurrencyAmount(price);
  const roundedPrice = roundToTwo(numericPrice);
  
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(roundedPrice);
  } catch (error) {
    console.error('Invalid currency code:', currency);
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(roundedPrice);
  }
}

export function formatPriceRange(variations: Variation[], currency: string = 'USD') {
  if (!variations || variations.length === 0) return '';

  const prices = variations.map(v => parseFloat(v.variation_amount));
  const minPrice = Math.min(...prices);
  const maxPrice = Math.max(...prices);

  if (minPrice === maxPrice) {
    return formatPrice(minPrice, currency);
  }

  return `${formatPrice(minPrice, currency)} - ${formatPrice(maxPrice, currency)}`;
}

export function getLowestPrice(variations: Variation[]) {
  if (!variations || variations.length === 0) return 0;
  return Math.min(...variations.map(v => parseFloat(v.variation_amount)));
}

export function truncateText(text: string, length: number) {
  if (text.length <= length) return text;
  return text.slice(0, length) + '...';
}

export function fetchWithTimeout(resource: string, options: RequestInit & { timeout?: number } = {}) {
  const { timeout = 10000 } = options;
  
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeout);

  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  
  try {
    const response = fetch(resource, {
      ...options,
      headers: {
        'Accept': 'application/json',
        ...(isIOS ? {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0',
          'If-None-Match': new Date().getTime().toString()
        } : {}),
        ...(options.headers || {})
      },
      ...(isIOS ? {
        signal: controller.signal,
        cache: 'no-store',
        credentials: 'omit'
      } : {
        signal: controller.signal
      })
    });
    clearTimeout(id);
    return response;
  } catch (error) {
    clearTimeout(id);
    if (error.name === 'AbortError') {
      throw new Error('Request timed out. Please check your connection and try again.');
    }
    throw error;
  }
}

export function setCache(key: string, data: any, days = 10) {
  const item = {
    value: data,
    timestamp: new Date().getTime() + (days * 24 * 60 * 60 * 1000)
  };
  try {
    localStorage.setItem(key, JSON.stringify(item));
  } catch (error) {
    console.warn('Failed to set cache:', error);
  }
}

export function getCache(key: string) {
  try {
    const item = localStorage.getItem(key);
    if (!item) return null;

    const { value, timestamp } = JSON.parse(item);
    if (new Date().getTime() > timestamp) {
      localStorage.removeItem(key);
      return null;
    }
    return value;
  } catch (error) {
    console.warn('Failed to get cache:', error);
    return null;
  }
}

export function setShippingPreferences(country: string, location: string) {
  setCache('shipping_country', country, 30);
  setCache('shipping_location', location, 30);
}

export function getShippingPreferences(): { country: string | null; location: string | null } {
  return {
    country: getCache('shipping_country'),
    location: getCache('shipping_location')
  };
}

export function filterLocations(locations: ShippingLocation[], search: string): ShippingLocation[] {
  const searchLower = search.toLowerCase();
  return locations.filter(
    location => 
      location.name?.toLowerCase().includes(searchLower) ||
      location.country.toLowerCase().includes(searchLower)
  );
}

export function copyToClipboard(text: string) {
  navigator.clipboard.writeText(text);
}

export function validateEmail(email: string): boolean {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(email);
}

export function validatePhone(phone: string): boolean {
  return phone.length >= 10 && /^\+?[\d\s-]+$/.test(phone);
}

export function getCountries(locations: ShippingLocation[]): string[] {
  return [...new Set(locations.map(location => location.country))];
}

export function getLocationsByCountry(locations: ShippingLocation[], country: string): ShippingLocation[] {
  return locations.filter(location => location.country === country);
}

export async function retryWithBackoff(fn: () => Promise<any>, maxRetries = 3, baseDelay = 1000): Promise<any> {
  let lastError;
  for (let i = 0; i < maxRetries; i++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;
      if (i === maxRetries - 1) break;
      await new Promise(resolve => setTimeout(resolve, baseDelay * Math.pow(2, i)));
    }
  }
  throw lastError;
}
